import { Check, Search } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { SecondaryButton } from "@/components/design-system";

/**
 * AssignmentDialogShell — canonical layout used by every "ata / ata / ata"
 * dialog on the Products page (Kalıp / Model / Görsel).  A single visual
 * family, a single interaction pattern:
 *
 *   • Header: product identifier ({SKU} · {ad}) and the assignment title.
 *   • Optional search input (searchable=true).
 *   • Scrollable option list (the caller renders <AssignOptionRow> children).
 *   • Footer: "Bağlantıyı kaldır" (when applicable) + "Kapat".
 *
 * Assignment happens on row-click and closes the dialog — no separate "Kaydet"
 * button anywhere.  This keeps mental cost minimal and removes the friction the
 * merchant flagged between the three legacy dialogs.
 */
export const AssignmentDialogShell = ({
    title, product, onClose, children,
    searchable = false, searchValue = "", onSearchChange, searchPlaceholder = "Ara…",
    onClear, testId,
}) => (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden" data-testid={testId}>
            <DialogTitle className="sr-only">{title}</DialogTitle>
            <div className="px-5 py-4 border-b border-border">
                <div className="font-display font-bold text-base">{title}</div>
                <div className="text-xs text-muted-foreground mt-1 truncate">
                    <span className="font-mono num text-foreground/80">{product.id}</span>
                    <span className="text-muted-foreground/60"> · </span>
                    {product.name || <span className="italic text-muted-foreground/70">İsimsiz ürün</span>}
                </div>
            </div>
            {searchable && (
                <div className="px-5 py-3 border-b border-border">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                        <Input
                            value={searchValue}
                            onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
                            placeholder={searchPlaceholder}
                            className="h-9 pl-9 text-xs bg-background"
                            data-testid={`${testId}-search`}
                        />
                    </div>
                </div>
            )}
            <div className="max-h-80 overflow-y-auto scroll-thin bg-muted px-5 py-4 space-y-2">
                {children}
            </div>
            <div className="px-5 py-3 border-t border-border flex items-center justify-between gap-2">
                {onClear ? (
                    <button
                        onClick={onClear}
                        className="text-xs text-muted-foreground hover:text-destructive"
                        data-testid={`${testId}-clear`}
                    >
                        Bağlantıyı kaldır
                    </button>
                ) : <span />}
                <SecondaryButton onClick={onClose} data-testid={`${testId}-close`}>
                    Kapat
                </SecondaryButton>
            </div>
        </DialogContent>
    </Dialog>
);

/**
 * AssignOptionRow — canonical row inside an AssignmentDialogShell. Renders as
 * a full-width button; caller lays out its own thumbnail + label. When
 * `current` is true a Check icon is dropped on the right so the merchant sees
 * exactly which choice is currently attached.
 */
export const AssignOptionRow = ({ current, onClick, disabled, testId, className = "", children }) => (
    <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        className={`w-full text-left flex items-center gap-3 p-3 rounded-lg bg-card border shadow-sm transition-all ${current ? "border-foreground shadow-md" : "border-border hover:border-foreground/40 hover:shadow-md"} ${disabled ? "opacity-60 cursor-not-allowed" : ""} ${className}`}
        data-testid={testId}
    >
        {children}
        {current && <Check className="h-4 w-4 text-brand shrink-0 ml-auto" strokeWidth={2.5} />}
    </button>
);
